import type { ReactNode } from 'react'

import { Box } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { MasterTableActionBar } from '~/components/common/MasterTableActionBar'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'

type Props = {
  /** true のときは表の領域だけを RegionLoading で覆う (初回 loading gate)。ツールバー・操作バーは覆わない。 */
  showInitialLoading: boolean
  /**
   * 上段 toolbar (MasterFilterToolbar / FilterToolbar 等の caller-composed node)。
   * タブごとに追加フィルタ (計画区分 / 失効トグル) の有無が異なるため slot として受ける。
   */
  filterToolbar: ReactNode
  /** 「追加」ボタン (design 準拠、主 = 個別入力を 1 クリックで即実行)。 */
  onAddIndividual: () => void
  /**
   * CSV ダウンロード handler。undefined のとき MasterTableActionBar 側で disabled 化される
   * (AC-05: List RPC 未取得時 = ローディング中またはエラー)。
   */
  onCsvExport: (() => void) | undefined
  /** CSV ダウンロードを押せない理由のツールチップ (読み込み中・読み込み失敗)。 MasterTableActionBar へそのまま渡す。 */
  csvExportDisabledReason?: string
  /** PageScrollArea 直下に置く一覧テーブル。 */
  table: ReactNode
  createModal: ReactNode
  /** modalState.kind !== 'edit' のとき null を渡す。 */
  editModal: ReactNode | null
  /** modalState.kind !== 'delete' のとき null を渡す。 */
  deleteDialog: ReactNode | null
  /**
   * true の時、 「追加」ボタン (spec の「新規登録」) を disabled にする (AC-OMB-025 / AC-MOI-005: BG タブ
   * 例外。 事業者マスタ 0 件で BG区分の選択肢が全て絞り込まれ 0 件になる時)。
   * 既定 false。 `MasterTableActionBar` へそのまま forward する。
   */
  addDisabled?: boolean
  /**
   * addDisabled=true の時の押せない理由。 画面上部の info Alert とボタンのツールチップの
   * 両方に同じ文言で出す。 未指定なら Alert もツールチップも出さない。
   */
  addDisabledReason?: string
}

/**
 * OCCTO マスタ 8 タブ共通の JSX 骨格。
 * 順序は design/admin/occto.html 準拠で
 * (押せない理由の Alert) → filterToolbar → MasterTableActionBar → PageScrollArea(table) → modals。
 *
 * 表右上の操作は全タブ共通で「CSV ダウンロード」(tertiary) + 「追加」(primary) の 2 つ
 * (design `admin/occto.html` と同じ文言。 spec index.md AC-MOI-003 は「CSVエクスポート」
 * 「新規登録」と書いているが、 8080・React mock・dev が揃っている design の文言を採る)。
 *
 * 画面内の CSV アップロード (▾) は全タブとも出さない (index.md AC-MOI-010〜011・020 は欠番、
 * #5474)。 [design差異] design は事業者・BG・計画提出者・発電所・計画系 3 タブで
 * 「追加 ▾ → CSV アップロード (列マッピングで取込)」を残しているが、 spec の撤去決定に従う。
 * 取引先タブで design が出している CSV 入出力画面への導線 2 本は、 遷移先 (取引先の CSV
 * アップロード画面) が dev に無いため置かない。
 *
 * 押せない理由 (BG タブ AC-OMB-025) は UI 辞書 action-reason の区分 1 (設定の不足)。
 * 画面上部の info Alert で出し、 ボタンは非活性 + 同じ理由のツールチップにする
 * (design `#occ-bg-blocked-alert` と同じ置き場所 = ツールバーの上)。 非活性・Alert・
 * ツールチップは addDisabled / addDisabledReason の同じ値 1 か所から切り替える。
 *
 * TODO(rbac): spec 権限テーブル「需給管理者のみ CUD 可」は未実装 (認可方針確立後に
 * MasterTableActionBar の Button を disabled / 非表示にする)。
 */
export const MasterCrudTabShell = ({
  showInitialLoading,
  filterToolbar,
  onAddIndividual,
  onCsvExport,
  csvExportDisabledReason,
  table,
  createModal,
  editModal,
  deleteDialog,
  addDisabled = false,
  addDisabledReason,
}: Props): React.ReactElement => {
  const addBlockedReason =
    addDisabled && addDisabledReason !== undefined && addDisabledReason !== ''
      ? addDisabledReason
      : undefined
  return (
    <>
      {addBlockedReason !== undefined && (
        // design `#occ-bg-blocked-alert`: 説明文の下・ツールバーの上、 ツールバーとの間 16px。
        // flexShrink=0: 残余高さを表が取る flex column の中で潰れないようにする。
        <Box flexShrink={0} marginBottom={4}>
          <CompactAlert status="info" title={addBlockedReason} />
        </Box>
      )}
      {filterToolbar}
      {/* showCsvUpload={false}: #5474 の撤去を共有部品ではなくこの呼び出し点に閉じ込め、
          撤去対象外の特異日カレンダーには波及させない。 */}
      <MasterTableActionBar
        addDisabled={addDisabled}
        addDisabledReason={addDisabledReason}
        csvExportDisabledReason={csvExportDisabledReason}
        onAddIndividual={onAddIndividual}
        onCsvExport={onCsvExport}
        showCsvUpload={false}
      />
      <RegionLoading
        flexDirection="column"
        flex="1"
        display="flex"
        minHeight={showInitialLoading ? '200px' : 0}
        isLoading={showInitialLoading}
      >
        {showInitialLoading ? null : <PageScrollArea>{table}</PageScrollArea>}
      </RegionLoading>
      {createModal}
      {editModal}
      {deleteDialog}
    </>
  )
}
